/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * THIS IS GENERATED CODE!
 * PLEASE DO NOT MODIFY - ALL YOUR WORK WOULD BE LOST!
 *
 * @author	Yann Massard, yamass@gmail.com
 * @author	Matthias Bernstein, matthias.bernstein@data-centrics.com
 */
export const typescriptDeclarationFixConstant = 1;

import {UiEvent} from "./UiEvent";
import {UiCommand} from "./UiCommand";
import {UiSubEvent} from "./UiSubEvent";
import {UiSubCommand} from "./UiSubCommand";
import {TeamAppsEvent} from "../modules/util/TeamAppsEvent";
import {UiComponentConfig} from "./UiComponentConfig";
import {UiMultiRecordFieldContainerConfig} from "./UiMultiRecordFieldContainerConfig";
import {UiTableColumnConfig} from "./UiTableColumnConfig";
import {UiColorConfig} from "./UiColorConfig";
import {UiSelectionFrameConfig} from "./UiSelectionFrameConfig";
import {UiFieldConfig} from "./UiFieldConfig";
import {UiSortDirection} from "./UiSortDirection";
import {UiMultiRecordFieldContainerCommandHandler} from "./UiMultiRecordFieldContainerConfig";
import {UiMultiRecordFieldContainerEventSource} from "./UiMultiRecordFieldContainerConfig";


export interface UiTableConfig extends UiComponentConfig, UiMultiRecordFieldContainerConfig {
	_type?: string;
	columns: UiTableColumnConfig[];
	idProperty: string;
	parentIdProperty?: string;
	displayAsList?: boolean;
	forceFitWidth?: boolean;
	rowHeight?: number;
	stripedRows?: boolean;
	stripedRowColorEven?: UiColorConfig;
	stripedRowColorOdd?: UiColorConfig;
	hideHeaders?: boolean;
	allowMultiRowSelection?: boolean;
	selectionColor?: UiColorConfig;
	rowBorderWidth?: number;
	rowBorderColor?: UiColorConfig;
	showRowCheckBoxes?: boolean;
	showNumbering?: boolean;
	showRecordIdNumbering?: boolean;
	iconPropertyName?: string;
	tableData?: any[];
	totalNumberOfRecords?: number;
	sortField?: string;
	sortDirection?: UiSortDirection;
	editable?: boolean;
	ensureEmptyLastRow?: boolean;
	treeMode?: boolean;
	expandedPropertyName?: string;
	lazyChildrenFlagProperty?: string;
	indentedColumnName?: string;
	indentation?: number;
	selectionFrame?: UiSelectionFrameConfig;
	showHeaderRow?: boolean;
	headerRowBorderWidth?: number;
	headerRowBorderColor?: UiColorConfig;
	headerRowHeight?: number;
	headerRowBackgroundColor?: UiColorConfig;
	headerRowFields?: UiFieldConfig[];
	headerRowData?: any;
	showFooterRow?: boolean;
	footerRowBorderWidth?: number;
	footerRowBorderColor?: UiColorConfig;
	footerRowHeight?: number;
	footerRowBackgroundColor?: UiColorConfig;
	footerRowFields?: UiFieldConfig[];
	footerRowData?: any
}

export interface UiTableCommandHandler extends UiMultiRecordFieldContainerCommandHandler {
	clearTable(): void;
	removeRow(recordId: any): void;
	setTableData(startIndex: number, data: any[], totalNumberOfRecords: number, sortField: string, sortDirection: UiSortDirection, clearTableCache: boolean): void;
	setChildrenData(parentRecordId: any, data: any[]): void;
	markTableField(recordId: any, fieldName: string, mark: boolean): void;
	clearAllFieldMarkings(): void;
	setRecordBold(recordId: any, bold: boolean): void;
	selectSingleRow(recordId: any, scrollToRecord: boolean): void;
	setHeaderData(data: any): void;
	setHeaderFieldValue(fieldName: string, value: any): void;
	focusHeaderField(fieldName: string): void;
	invokeOnHeaderField(fieldName: string, fieldCommand: any): void;
	setFooterData(data: any): void;
	setFooterFieldValue(fieldName: string, value: any): void;
	focusFooterField(fieldName: string): void;
	invokeOnFooterField(fieldName: string, fieldCommand: any): void;
}

export interface UiTableEventSource extends UiMultiRecordFieldContainerEventSource {
	onCellEditingStarted: TeamAppsEvent<UiTable_CellEditingStartedEvent>;
	onCellEditingStopped: TeamAppsEvent<UiTable_CellEditingStoppedEvent>;
	onRowSelected: TeamAppsEvent<UiTable_RowSelectedEvent>;
	onMultipleRowsSelected: TeamAppsEvent<UiTable_MultipleRowsSelectedEvent>;
	onSortingChanged: TeamAppsEvent<UiTable_SortingChangedEvent>;
	onTableDataRequest: TeamAppsEvent<UiTable_TableDataRequestEvent>;
	onRequestNestedData: TeamAppsEvent<UiTable_RequestNestedDataEvent>;
	onFieldOrderChange: TeamAppsEvent<UiTable_FieldOrderChangeEvent>;
	onColumnSizeChange: TeamAppsEvent<UiTable_ColumnSizeChangeEvent>;
	onHeaderFieldValueChanged: TeamAppsEvent<UiTable_HeaderFieldValueChangedEvent>;
	onHeaderFieldEvent: TeamAppsEvent<UiTable_HeaderFieldEventEvent>;
	onFooterFieldValueChanged: TeamAppsEvent<UiTable_FooterFieldValueChangedEvent>;
	onFooterFieldEvent: TeamAppsEvent<UiTable_FooterFieldEventEvent>;
}

export interface UiTable_CellEditingStartedEvent extends UiEvent {
	_type: "UiTable.cellEditingStarted";
	redundancyKey?: string;
	recordId: any;
	fieldName: string;
	currentValue: any
}

export interface UiTable_CellEditingStoppedEvent extends UiEvent {
	_type: "UiTable.cellEditingStopped";
	redundancyKey?: string;
	recordId: any;
	fieldName: string
}

export interface UiTable_RowSelectedEvent extends UiEvent {
	_type: "UiTable.rowSelected";
	redundancyKey?: string;
	recordId: any;
	isRightMouseButton: boolean;
	isDoubleClick: boolean
}

export interface UiTable_MultipleRowsSelectedEvent extends UiEvent {
	_type: "UiTable.multipleRowsSelected";
	redundancyKey?: string;
	recordIds: any[]
}

export interface UiTable_SortingChangedEvent extends UiEvent {
	_type: "UiTable.sortingChanged";
	redundancyKey?: string;
	sortField: string;
	sortDirection: UiSortDirection
}

export interface UiTable_TableDataRequestEvent extends UiEvent {
	_type: "UiTable.tableDataRequest";
	redundancyKey?: string;
	startIndex: number;
	length: number;
	sortField: string;
	sortDirection: UiSortDirection
}

export interface UiTable_RequestNestedDataEvent extends UiEvent {
	_type: "UiTable.requestNestedData";
	redundancyKey?: string;
	recordId: any
}

export interface UiTable_FieldOrderChangeEvent extends UiEvent {
	_type: "UiTable.fieldOrderChange";
	redundancyKey?: string;
	fieldName: string;
	position: number
}

export interface UiTable_ColumnSizeChangeEvent extends UiEvent {
	_type: "UiTable.columnSizeChange";
	redundancyKey?: string;
	fieldName: string;
	size: number
}

export interface UiTable_HeaderFieldValueChangedEvent extends UiEvent {
	_type: "UiTable.headerFieldValueChanged";
	redundancyKey?: string;
	fieldName: string;
	value: any
}

export interface UiTable_HeaderFieldEventEvent extends UiEvent {
	_type: "UiTable.headerFieldEvent";
	redundancyKey?: string;
	fieldName: string;
	fieldEvent: UiSubEvent
}

export interface UiTable_FooterFieldValueChangedEvent extends UiEvent {
	_type: "UiTable.footerFieldValueChanged";
	redundancyKey?: string;
	fieldName: string;
	value: any
}

export interface UiTable_FooterFieldEventEvent extends UiEvent {
	_type: "UiTable.footerFieldEvent";
	redundancyKey?: string;
	fieldName: string;
	fieldEvent: UiSubEvent
}

